import { createElement } from '../../../core/dom/helpers.js';
import { createReorderControls } from '../../../core/dom/reorder-controls.js';
import { FIELD_TYPES, isChoiceType, isInvalidField } from '../../../core/form-builder/form-structure.js';

/**
 * @typedef {import('../../../contracts/forms.js').FormBuilderField} FormBuilderField
 * @typedef {import('../../../contracts/forms.js').FormBuilderCssClass} FormBuilderCssClass
 */

/** Human labels for the six legacy field types (display only — the value is the cssClass). */
const TYPE_LABELS = /** @type {Record<FormBuilderCssClass, string>} */ ({
  input_text: 'Text field',
  email: 'Email',
  textarea: 'Paragraph (textarea)',
  checkbox: 'Checkbox group',
  radio: 'Radio group',
  select: 'Select list',
});

/**
 * ONE form-builder field row — a card for a single field inside a fieldset. Sibling of
 * `video-card.js` (same keyed-callback / disabled / setMoveState / destroy contract); the
 * difference is that the editor is polymorphic over the six `cssClass` types:
 *
 * - simple (`input_text`/`email`/`textarea`): a single Label input.
 * - choice (`checkbox`/`radio`/`select`): a group Title + an ordered options editor (each option
 *   has a value + a "default" baseline toggle, with add/remove/reorder); `select` adds a
 *   "Allow multiple" checkbox.
 *
 * The row owns its OWN nested option keys (`op-`) and option DOM. The parent driver owns the
 * field key (`fd-`) and re-resolves it on every callback. Changing the type re-renders only the
 * type-specific body of this card (label↔title/options), never the whole list.
 *
 * Inline validity: a choice field that is half-specified (title without options, or options
 * without a title) shows a `role="alert"` hint and sets `aria-invalid` on the offending input —
 * recomputed via the shared `isInvalidField` predicate (no second rule).
 *
 * @param {Object} args
 * @param {string} args.key - Stable field key the callbacks echo back.
 * @param {FormBuilderField} args.value
 * @param {(key: string, patch: Partial<FormBuilderField>) => void} args.onChange - Field-level patch (cssClass/label/title/required/multiple).
 * @param {(key: string) => void} args.onRemove
 * @param {(key: string) => void} args.onMoveUp
 * @param {(key: string) => void} args.onMoveDown
 * @returns {{ el: HTMLElement, handle: HTMLElement, setDisabled: (d: boolean) => void, setMoveState: (canUp: boolean, canDown: boolean) => void, focusMove: (preferUp: boolean) => void, refreshValidity: () => void, destroy: () => void }}
 */
export function createFieldRow({ key, value, onChange, onRemove, onMoveUp, onMoveDown }) {
  let disabled = false;

  /** Live working copy of THIS field (the parent is the source of truth, but the row needs
   * the current cssClass/options to render its body + compute validity). */
  let current = value;

  /** Monotonic option-key source — option keys are this row's private state. */
  let optKeySeq = 0;
  const nextOptKey = () => `op-${key}-${++optKeySeq}`;
  /** @type {string[]} Option keys, lockstep with current.options. */
  let optionKeys = (current.options ?? []).map(() => nextOptKey());

  const row = createElement('div', {
    class: 'fb-field', role: 'group',
  });

  // Reorder controls: drag handle (mouse) + Move up/down (keyboard/touch).
  const reorder = createReorderControls({ key, ariaNoun: 'field', orientation: 'column', onMoveUp, onMoveDown });
  const controls = reorder.el;
  // withHandle defaults true → the handle is always present (the row's contract stays non-null).
  const handle = /** @type {HTMLElement} */ (reorder.handle);

  // Field body (type selector + per-type editor).
  const body = createElement('div', { class: 'fb-field__body' });

  // Type selector.
  const typeId = `fbfield-type-${key}`;
  const typeLabel = createElement('label', { class: 'fb-field__label', for: typeId }, ['Field type']);
  const typeSelect = /** @type {HTMLSelectElement} */ (createElement('select', {
    id: typeId, class: 'fb-field__type',
  }, FIELD_TYPES.map((t) => {
    const opt = /** @type {HTMLOptionElement} */ (createElement('option', { value: t }, [TYPE_LABELS[t]]));
    if (t === current.cssClass) opt.selected = true;
    return opt;
  })));

  // Required toggle (shared by all types).
  const requiredWrap = createElement('label', { class: 'fb-field__required' });
  const requiredInput = /** @type {HTMLInputElement} */ (createElement('input', { type: 'checkbox', class: 'fb-field__required-input' }));
  requiredInput.checked = Boolean(current.required);
  requiredWrap.appendChild(requiredInput);
  requiredWrap.appendChild(createElement('span', {}, ['Required']));

  // The type-specific editor slot (rebuilt on type change).
  const editor = createElement('div', { class: 'fb-field__editor' });

  // Inline validity hint (choice half-specified).
  const hintId = `fbfield-hint-${key}`;
  const hint = createElement('div', { id: hintId, class: 'fb-field__hint', role: 'alert', hidden: 'hidden' });

  // Corner × remove.
  const removeBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button', class: 'fb-field__remove', 'aria-label': 'Remove field',
  }, ['×']));

  // --- per-type editor build -------------------------------------------------

  /** @type {HTMLInputElement | null} The label/title input that carries aria-invalid for choice fields. */
  let primaryInput = null;
  /** @type {HTMLElement | null} */ let optionsList = null;

  /** Rebuild the editor slot for the current cssClass. */
  function renderEditor() {
    // A choice→simple transition wipes the options subtree here WITHOUT calling renderOptions, so
    // release any live option controls' listeners first (else they leak on every type switch).
    destroyOptionControls();
    editor.textContent = '';
    primaryInput = null;
    optionsList = null;

    if (isChoiceType(current.cssClass)) {
      // Group title.
      const titleId = `fbfield-title-${key}`;
      const titleLabel = createElement('label', { class: 'fb-field__label', for: titleId }, ['Group title']);
      const titleInput = /** @type {HTMLInputElement} */ (createElement('input', {
        type: 'text', id: titleId, class: 'fb-field__title', placeholder: 'Group title',
      }));
      titleInput.value = current.title ?? '';
      titleInput.disabled = disabled;
      titleInput.addEventListener('input', () => {
        if (disabled) return;
        current = { ...current, title: titleInput.value };
        onChange(key, { title: titleInput.value });
        refreshValidity();
      });
      primaryInput = titleInput;
      editor.appendChild(titleLabel);
      editor.appendChild(titleInput);

      // Select-only "allow multiple".
      if (current.cssClass === 'select') {
        const multWrap = createElement('label', { class: 'fb-field__multiple' });
        const multInput = /** @type {HTMLInputElement} */ (createElement('input', { type: 'checkbox', class: 'fb-field__multiple-input' }));
        multInput.checked = Boolean(current.multiple);
        multInput.disabled = disabled;
        multInput.addEventListener('change', () => {
          if (disabled) return;
          current = { ...current, multiple: multInput.checked };
          onChange(key, { multiple: multInput.checked });
        });
        multWrap.appendChild(multInput);
        multWrap.appendChild(createElement('span', {}, ['Allow multiple selection']));
        editor.appendChild(multWrap);
      }

      // Options editor.
      const optsLabel = createElement('div', { class: 'fb-field__label' }, ['Options']);
      optionsList = createElement('div', { class: 'fb-field__options', role: 'group', 'aria-label': 'Options' });
      const addOptBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
        type: 'button', class: 'fb-field__add-option btn btn-secondary',
      }, ['Add option']));
      addOptBtn.disabled = disabled;
      addOptBtn.addEventListener('click', () => {
        if (disabled) return;
        current = { ...current, options: [...(current.options ?? []), { value: '', baseline: false }] };
        optionKeys = [...optionKeys, nextOptKey()];
        onChange(key, { options: current.options });
        renderOptions();
        refreshValidity();
      });
      editor.appendChild(optsLabel);
      editor.appendChild(optionsList);
      editor.appendChild(addOptBtn);
      renderOptions();
    } else {
      // Simple types: a single Label input.
      const labelId = `fbfield-label-${key}`;
      const labelLabel = createElement('label', { class: 'fb-field__label', for: labelId }, ['Label']);
      const labelInput = /** @type {HTMLInputElement} */ (createElement('input', {
        type: 'text', id: labelId, class: 'fb-field__label-input', placeholder: 'Field label',
      }));
      labelInput.value = current.label ?? '';
      labelInput.disabled = disabled;
      labelInput.addEventListener('input', () => {
        if (disabled) return;
        current = { ...current, label: labelInput.value };
        onChange(key, { label: labelInput.value });
        refreshValidity();
      });
      primaryInput = labelInput;
      editor.appendChild(labelLabel);
      editor.appendChild(labelInput);
    }
  }

  /** Live option reorder-controls, keyed by optKey — destroyed on every rebuild/teardown so
   * their listeners never leak (removing the DOM node alone does not detach them). */
  /** @type {Map<string, ReturnType<typeof createReorderControls>>} */
  const optionControls = new Map();
  function destroyOptionControls() {
    for (const c of optionControls.values()) c.destroy();
    optionControls.clear();
  }

  /** Reorder an option by key, re-deriving from the resolved indices. @param {string} optKey @param {number} dir -1 = up, +1 = down */
  function moveOption(optKey, dir) {
    const from = optionKeys.indexOf(optKey);
    const to = from + dir;
    if (from === -1 || to < 0 || to >= current.options.length) return;
    const next = [...current.options]; const nk = [...optionKeys];
    const [mv] = next.splice(from, 1); const [mk] = nk.splice(from, 1);
    next.splice(to, 0, mv); nk.splice(to, 0, mk);
    current = { ...current, options: next };
    optionKeys = nk;
    onChange(key, { options: next });
    renderOptions();
    // Re-home keyboard focus onto the moved option's Move button (the rebuild dropped it).
    optionControls.get(optKey)?.focusMove(dir < 0);
  }

  /** Rebuild the options list (choice types only). Each option = move up/down + value + default + ×. */
  function renderOptions() {
    if (!optionsList) return;
    destroyOptionControls(); // release the prior rows' listeners before rebuilding
    const listEl = optionsList; // captured non-null for the forEach closure below
    listEl.textContent = '';
    const opts = current.options ?? [];
    const lastIdx = opts.length - 1;
    opts.forEach((opt, i) => {
      const optKey = optionKeys[i];
      const optRow = createElement('div', { class: 'fb-option' });

      // Option reorder = ▲/▼ Move buttons only (no drag handle) via the shared controls.
      const optReorder = createReorderControls({
        key: optKey, ariaNoun: 'option', orientation: 'row', withHandle: false,
        onMoveUp: (k) => moveOption(k, -1), onMoveDown: (k) => moveOption(k, 1),
      });
      optionControls.set(optKey, optReorder);
      optReorder.setMoveState(i > 0, i < lastIdx);
      optReorder.setDisabled(disabled);

      const valInput = /** @type {HTMLInputElement} */ (createElement('input', {
        type: 'text', class: 'fb-option__value', placeholder: 'Option value', 'aria-label': 'Option value',
      }));
      valInput.value = opt.value ?? '';
      valInput.disabled = disabled;
      valInput.addEventListener('input', () => {
        if (disabled) return;
        const idx = optionKeys.indexOf(optKey);
        if (idx === -1) return;
        const next = current.options.map((o, j) => (j === idx ? { ...o, value: valInput.value } : o));
        current = { ...current, options: next };
        onChange(key, { options: next });
        refreshValidity();
      });

      const baseWrap = createElement('label', { class: 'fb-option__default' });
      const baseInput = /** @type {HTMLInputElement} */ (createElement('input', {
        type: 'checkbox', class: 'fb-option__default-input', 'aria-label': 'Selected by default',
      }));
      baseInput.checked = Boolean(opt.baseline);
      baseInput.disabled = disabled;
      baseInput.addEventListener('change', () => {
        if (disabled) return;
        const idx = optionKeys.indexOf(optKey);
        if (idx === -1) return;
        const next = current.options.map((o, j) => (j === idx ? { ...o, baseline: baseInput.checked } : o));
        current = { ...current, options: next };
        onChange(key, { options: next });
      });
      baseWrap.appendChild(baseInput);
      baseWrap.appendChild(createElement('span', {}, ['Default']));

      const optRemove = /** @type {HTMLButtonElement} */ (createElement('button', {
        type: 'button', class: 'fb-option__remove', 'aria-label': 'Remove option',
      }, ['×']));
      optRemove.disabled = disabled;
      optRemove.addEventListener('click', () => {
        if (disabled) return;
        const idx = optionKeys.indexOf(optKey);
        if (idx === -1) return;
        const next = [...current.options.slice(0, idx), ...current.options.slice(idx + 1)];
        current = { ...current, options: next };
        optionKeys = [...optionKeys.slice(0, idx), ...optionKeys.slice(idx + 1)];
        onChange(key, { options: next });
        renderOptions();
        refreshValidity();
      });

      optRow.appendChild(optReorder.el);
      optRow.appendChild(valInput);
      optRow.appendChild(baseWrap);
      optRow.appendChild(optRemove);
      listEl.appendChild(optRow);
    });
  }

  /** Recompute the inline validity hint + aria-invalid from the shared predicate. */
  function refreshValidity() {
    const invalid = isInvalidField(current);
    hint.hidden = !invalid;
    if (invalid) {
      // Name the missing piece: title present without options, or options without a title.
      const hasTitle = (current.title ?? '').trim() !== '';
      hint.textContent = hasTitle ? 'Add at least one option' : 'Add a group title';
    } else {
      hint.textContent = '';
    }
    if (primaryInput) {
      primaryInput.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      if (invalid) primaryInput.setAttribute('aria-describedby', hintId);
      else primaryInput.removeAttribute('aria-describedby');
    }
    updateAriaLabel();
  }

  /** Keep the row's group label descriptive of its current content. */
  function updateAriaLabel() {
    const name = (isChoiceType(current.cssClass) ? current.title : current.label) || TYPE_LABELS[current.cssClass];
    row.setAttribute('aria-label', `Field: ${name}`);
  }

  typeSelect.addEventListener('change', () => {
    if (disabled) return;
    const next = /** @type {FormBuilderCssClass} */ (typeSelect.value);
    current = { ...current, cssClass: next };
    onChange(key, { cssClass: next });
    renderEditor();
    refreshValidity();
  });

  requiredInput.addEventListener('change', () => {
    if (disabled) return;
    current = { ...current, required: requiredInput.checked };
    onChange(key, { required: requiredInput.checked });
  });

  body.appendChild(typeLabel);
  body.appendChild(typeSelect);
  body.appendChild(requiredWrap);
  body.appendChild(editor);
  body.appendChild(hint);

  row.appendChild(controls);
  row.appendChild(body);
  row.appendChild(removeBtn);

  removeBtn.addEventListener('click', () => {
    if (disabled) return;
    onRemove(key);
  });

  renderEditor();
  refreshValidity();

  return {
    el: row,
    handle,
    /** @param {boolean} d */
    setDisabled(d) {
      disabled = d;
      typeSelect.disabled = d;
      requiredInput.disabled = d;
      removeBtn.disabled = d;
      reorder.setDisabled(d); // field-level handle draggable + move-button disabled
      // The editor body's inputs (incl. option controls) were created with the current `disabled`;
      // re-render to apply.
      renderEditor();
      refreshValidity();
      row.classList.toggle('fb-field--disabled', d);
    },
    /** @param {boolean} up @param {boolean} down */
    setMoveState: reorder.setMoveState,
    /** @param {boolean} preferUp — re-home focus onto a field Move button after a reorder rebuilt the row. */
    focusMove: reorder.focusMove,
    refreshValidity,
    destroy() {
      destroyOptionControls();
      reorder.destroy();
      row.remove();
    },
  };
}
